import React, { useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Point, degToRad, dot, norm, subtract } from '../../store/algebrics';

interface ScrollProps {
  $thumbWidth: string;
  $angle: number;
  $transition: number;
}

const ScrollContainer = styled.div<ScrollProps>`
  ${({ $angle }) => {
    return css`
      display: flex;
      position: absolute;
      top: 3px;
      border-radius: 15px;
      width: -webkit-fill-available;
      height: 20px;
      background-color: rgb(236, 236, 236, 0.5);
      cursor: pointer;
      transform: rotate(${$angle}deg);
    `;
  }}
`;

const ScrollThumb = styled.div<ScrollProps>`
  ${({ $transition }) => {
    return css`
      position: relative;
      display: flex;
      border-radius: 15px;
      margin-top: auto;
      margin-bottom: auto;
      min-width: 10%;
      height: 15px;
      background-color: transparent;
      cursor: grab;
      transition: margin-left ${$transition}ms;
      &::after {
        position: absolute;
        border-radius: 15px;
        content: '';
        height: 100%;
        background-color: #513389;
        transition: width 0.1s;
        width: calc(100% - 6px);
        margin-left: 3px;
      }
    `;
  }}
`;

export interface DraggableScrollProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  value?: number;
  rotation?: number;
  setValue?: (v: number) => void;
  thumbWidth?: string;
}

const DraggableScroll = ({
  value,
  setValue,
  thumbWidth,
  rotation = 0,
  ...props
}: DraggableScrollProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [thumbLeft, setThumbLeft] = useState(0);
  const [oldPosition, setOldPosition] = useState<Point>({ x: 0, y: 0 });
  const [versor, setVersor] = useState({ x: 1, y: 0 });
  const [transition, setTransition] = useState(0);
  const [width, setThumbWidth] = useState(0);

  useEffect(() => {
    if (thumbRef.current) {
      setThumbWidth(thumbRef.current.offsetWidth);
    }
  }, [thumbRef?.current?.offsetWidth]);

  useEffect(() => {
    setVersor({
      x: Math.cos(degToRad(rotation)),
      y: Math.sin(degToRad(rotation)),
    });
  }, []);

  useEffect(() => {
    if (sliderRef.current && typeof value === 'number') {
      const newThumbLeft = Math.min(
        Math.max((sliderRef.current.offsetWidth - width) * value, 0),
        sliderRef.current.offsetWidth - width,
      );
      setThumbLeft(newThumbLeft);
    }
  }, [value, sliderRef?.current?.offsetWidth, thumbRef?.current?.offsetWidth]);

  const handleMouseMove = ({ clientX: x, clientY: y }: MouseEvent) => {
    if (isDragging && sliderRef.current) {
      const newPosition = { x, y };
      const displacement = subtract(newPosition, oldPosition);
      const direction = Math.sign(dot(versor, displacement));
      const maxThumbLeft = sliderRef.current.offsetWidth - width;
      const newThumbLeft = Math.min(
        Math.max(thumbLeft + direction * norm(displacement), 0),
        maxThumbLeft,
      );
      if (setValue) {
        setValue(newThumbLeft / maxThumbLeft);
      } else {
        setThumbLeft(newThumbLeft);
      }
    }
  };

  const handleThumbMouseDown = (
    client: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    const { clientX: x, clientY: y } = client;
    client.stopPropagation();
    setIsDragging(true);
    setTransition(0);
    setOldPosition({ x, y });
  };

  const handleMouseDown = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    setTransition(200);
    if (sliderRef.current) {
      const maxThumbLeft = sliderRef.current.offsetWidth - width;
      const offsetX = event.nativeEvent.offsetX;
      const newThumbLeft = Math.min(
        Math.max(offsetX - (offsetX > thumbLeft ? width : 0), 0),
        maxThumbLeft,
      );
      if (setValue) {
        setValue(newThumbLeft / maxThumbLeft);
      } else {
        setThumbLeft(newThumbLeft);
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const scrollProps: ScrollProps = {
    $thumbWidth: `${width}`,
    $angle: rotation,
    $transition: transition,
  };
  return (
    <ScrollContainer
      ref={sliderRef}
      onMouseDown={handleMouseDown}
      {...scrollProps}
      {...props}
    >
      <ScrollThumb
        ref={thumbRef}
        onMouseDown={handleThumbMouseDown}
        style={{
          marginLeft: `${thumbLeft}px`,
          width: thumbWidth ?? 'auto',
        }}
        {...scrollProps}
      />
    </ScrollContainer>
  );
};

export default DraggableScroll;
